import React, { useCallback, useMemo, useState } from "react";

import Button, { ButtonTypes } from "../../../../../components/Button";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../../utils/constants";
import {
  BASE_FILTER_OPTIONS,
  CASHBACK_FILTER_OPTION,
  DEFAULT_PAGE_SIZE,
  EMPTY_RESULTS_TEXT,
  NEUTRAL_LEGEND,
} from "./constants";
import { ProgressDetails } from "./ProgressDetails";
import { ProgressPager } from "./ProgressPager";
import { ProgressTableCard } from "./ProgressTableCard";
import { ProgressTableRow } from "./ProgressTableRow";
import { ProgressToolbar } from "./ProgressToolbar";
import { filterRows } from "./search";
import {
  EmptyResults,
  EmptyText,
  Header,
  HeaderCell,
  HiddenLabel,
  Legend,
  MobileList,
  Subtitle,
  Table,
  TableCard,
  TableScroll,
  Title,
} from "./styles";
import { ProgressFilter, ProgressRow, ProgressTableProps } from "./types";

const ProgressTableComponent: React.FC<ProgressTableProps> = ({
  rows,
  onEdit,
  onDuplicate,
  onDelete,
  onDeleteCorrection,
  onOpenExpense,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tablet);
  const [filter, setFilter] = useState<ProgressFilter>("all");
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const openDetails = useCallback((row: ProgressRow) => {
    setOpenKey(row.key);
  }, []);
  const actions = useMemo(
    () => ({
      onView: openDetails,
      onEdit,
      onDuplicate,
      onDelete,
      onDeleteCorrection,
      onOpenExpense,
    }),
    [
      openDetails,
      onEdit,
      onDuplicate,
      onDelete,
      onDeleteCorrection,
      onOpenExpense,
    ],
  );
  const hasCashback = rows.some(({ groups }) => groups.includes("cashback"));
  const filterOptions = hasCashback
    ? [...BASE_FILTER_OPTIONS, CASHBACK_FILTER_OPTION]
    : BASE_FILTER_OPTIONS;
  const activeFilter = hasCashback || filter !== "cashback" ? filter : "all";
  const matches = useMemo(
    () => filterRows(rows, { filter: activeFilter, query }),
    [rows, activeFilter, query],
  );

  const pageCount = Math.max(1, Math.ceil(matches.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * pageSize;
  const shownRows = matches.slice(start, start + pageSize);
  const isNarrowed = query.trim() !== "" || activeFilter !== "all";
  const hasNeutralRows = rows.some(({ isNeutral }) => isNeutral);
  const openRow = rows.find(({ key }) => key === openKey) ?? null;

  const changeFilter = useCallback((next: ProgressFilter) => {
    setFilter(next);
    setPage(0);
  }, []);
  const changeQuery = useCallback((next: string) => {
    setQuery(next);
    setPage(0);
  }, []);
  const changePageSize = useCallback((next: number) => {
    setPageSize(next);
    setPage(0);
  }, []);
  const resetFilters = useCallback(() => {
    setFilter("all");
    setQuery("");
    setPage(0);
  }, []);
  const closeDetails = useCallback(() => setOpenKey(null), []);

  let body: React.ReactNode;
  if (shownRows.length === 0) {
    body = (
      <EmptyResults>
        <EmptyText>{EMPTY_RESULTS_TEXT}</EmptyText>
        <Button type={ButtonTypes.link} onClick={resetFilters}>
          Reset filters
        </Button>
      </EmptyResults>
    );
  } else if (isCompact) {
    body = (
      <MobileList>
        {shownRows.map((row) => (
          <ProgressTableCard key={row.key} row={row} onOpen={openDetails} />
        ))}
      </MobileList>
    );
  } else {
    body = (
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <HeaderCell scope="col" $width="13%">
                Date
              </HeaderCell>
              <HeaderCell scope="col" $width="19%">
                Type
              </HeaderCell>
              <HeaderCell scope="col" $width="32%">
                Details
              </HeaderCell>
              <HeaderCell scope="col" $width="14%" $isNumeric>
                Amount
              </HeaderCell>
              <HeaderCell scope="col" $width="15%" $isNumeric>
                Balance after
              </HeaderCell>
              <HeaderCell scope="col" $width="7%">
                <HiddenLabel>Actions</HiddenLabel>
              </HeaderCell>
            </tr>
          </thead>
          <tbody>
            {shownRows.map((row) => (
              <ProgressTableRow
                key={row.key}
                row={row}
                actions={actions}
                onOpen={openDetails}
              />
            ))}
          </tbody>
        </Table>
      </TableScroll>
    );
  }

  return (
    <TableCard aria-label="Saving progress entries">
      <Header>
        <Title>Progress</Title>
        <Subtitle>
          All entries, newest first. The chart range doesn't filter this list.
        </Subtitle>
      </Header>
      <ProgressToolbar
        filter={activeFilter}
        filterOptions={filterOptions}
        query={query}
        isNarrowed={isNarrowed}
        isCompact={isCompact}
        onFilterChange={changeFilter}
        onQueryChange={changeQuery}
        onReset={resetFilters}
      />
      {body}
      {hasNeutralRows && <Legend>{NEUTRAL_LEGEND}</Legend>}
      <ProgressPager
        page={currentPage}
        pageCount={pageCount}
        pageSize={pageSize}
        start={start}
        shownCount={shownRows.length}
        matchCount={matches.length}
        totalCount={rows.length}
        isNarrowed={isNarrowed}
        isCompact={isCompact}
        onPageChange={setPage}
        onPageSizeChange={changePageSize}
      />
      <ProgressDetails row={openRow} actions={actions} onClose={closeDetails} />
    </TableCard>
  );
};

export const ProgressTable = React.memo(ProgressTableComponent);
